import { FC, useEffect } from "react";

import { VStack } from "@chakra-ui/react";
import { Form } from "formik";
import { useTranslation } from "next-i18next";

import { Button } from "@components/buttons";
import Checkbox from "@components/Checkbox";
import { Divider, Typography } from "@components/common";
import EmailInput from "@components/EmailInput";
import Link from "@components/Link";
import PasswordInput from "@components/PasswordInput";
import PhoneInput from "@components/PhoneInput";
import TextInput from "@components/TextInput";
import { useRouter } from "@lib/router";
import responsiveStyle from "@lib/theme/responsiveStyle";
import { DOMAIN, CONFIG } from "@utils/constants";

import { TRegistrationFormFieldsProps } from "../types";
import { FIELD_NAMES } from "../utils/constants";

const vStackSyle = {
  alignItems: "stretch",
  maxWidth: responsiveStyle({ mobile: "100%", desktop: "22.25rem" }),
  marginLeft: "auto",
  marginRight: "auto",
};
const centeredStyle = { alignSelf: "center" };

const RegistrationFormFields: FC<TRegistrationFormFieldsProps> = ({ isLoading, setErrors, errors }) => {
  const { t } = useTranslation();
  const { defaultLocale } = useRouter();

  useEffect(() => {
    if (errors) {
      setErrors(errors);
    }
  }, [setErrors, errors]);

  return (
    <Form noValidate method={'post'}>
      <VStack spacing="1.25rem" sx={vStackSyle}>
        <Typography type="sectionHeading" sx={centeredStyle}>
          {t("registration-form-create_an_account")}
        </Typography>
        <TextInput
          name={FIELD_NAMES.FULL_NAME}
          placeholder={t("registration-field_names-full_name")}
          label={t("registration-field_names-full_name")}
        />
        <PhoneInput
          name={FIELD_NAMES.MOBILE_NUMBER}
          placeholder={t("registration-field_names-mobile_number")}
          label={t("registration-field_names-mobile_number")}
          id="registration-form-phone-input"
          pinnedPhoneCodes={DOMAIN.DOMAIN_PHONE_NUMBERS[defaultLocale ?? ""]}
        />
        <EmailInput
          name={FIELD_NAMES.EMAIL}
          placeholder={t("registration-field_names-email")}
          label={t("registration-field_names-email")}
        />
        <PasswordInput
          name={FIELD_NAMES.PASSWORD}
          hint={t("registration-hints-password")}
          placeholder={t("registration-field_names-password")}
          label={t("registration-field_names-password")}
          validation={CONFIG.PASSWORD_VALIDATION}
          hasPreview
        />
        <PasswordInput
          name={FIELD_NAMES.PASSWORD_REPEAT}
          placeholder={t("registration-field_names-repeat_password")}
          label={t("registration-field_names-repeat_password")}
          hasPreview
        />
      </VStack>
      <Divider my="1.25rem" />
      <VStack spacing="1.25rem" sx={vStackSyle}>
        <Checkbox name={FIELD_NAMES.AGREEMENT} alignItems="flex-start">
          <Typography sx={{ fontSize: responsiveStyle({ mobile: "0.875rem", desktop: "1rem" }) }}>
            {t("registration-checkbox-i_have_read_the")}{" "}
            <Link
              label={t("registration-links-permitted_dimensions")}
              href={t("registration-links-permitted_dimensions_href")}
              variant="red"
              isExternal
              isInline
              sx={{ fontSize: responsiveStyle({ mobile: "0.875rem", desktop: "1rem" }) }}
            />
            {` ${t("registration-checkbox-and")} `}
            <Link
              label={t("registration-links-shipment_conditions")}
              href={t("registration-links-shipment_conditions_href")}
              variant="red"
              isExternal
              isInline
              sx={{ fontSize: responsiveStyle({ mobile: "0.875rem", desktop: "1rem" }) }}
            />
          </Typography>
        </Checkbox>
        <Checkbox name={FIELD_NAMES.MARKETING} alignItems="flex-start">
          <Typography sx={{ fontSize: responsiveStyle({ mobile: "0.875rem", desktop: "1rem" }) }}>
            {t("registration-checkbox-i_agree_receive_marketing_information")}
          </Typography>
        </Checkbox>
        <Button
          label={t("registration-button-register")}
          id="registration-form-submit-button"
          type="submit"
          isLoading={isLoading}
          sx={{
            alignSelf: responsiveStyle({ mobile: "stretch", desktop: "center" }),
            fontSize: responsiveStyle({ mobile: "0.875rem", desktop: "1rem" }),
            px: "2rem",
          }}
        />
      </VStack>
    </Form>
  );
};
export default RegistrationFormFields;
